import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";

const VALID_STATUSES = [
  "pending",
  "in_progress",
  "ready",
  "delivered",
  "closed",
  "cancelled",
];

export const getActiveOrdersSkill: Skill = {
  id: "getActiveOrders",
  description:
    "Obtiene órdenes activas (no cerradas) de un restaurante. Útil cuando el usuario pregunta '¿qué pedidos tengo activos?', '¿cuáles están en cocina?', etc. Filtra por status si se indica.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string", description: "ID del restaurante" },
      status: {
        type: "string",
        enum: VALID_STATUSES,
        description:
          "Filtra por status (opcional). Si se omite, devuelve no-cerradas.",
      },
    },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const status = args.status ? String(args.status) : undefined;
    if (!restaurantId) {
      return { ok: false, error: "Falta restaurantId." };
    }

    if (ctx.follow.enabled) {
      await ctx.follow.step(
        status ? `Consultando órdenes ${status}…` : "Consultando órdenes activas…",
        { durationMs: 500 },
      );
    }

    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      const { data } = await apiWithAuth.get(
        `/restaurants/${restaurantId}/orders?${params.toString()}`,
        ctx.accessToken,
      );
      const items: any[] = data?.items ?? data?.data ?? data ?? [];
      const active = (status
        ? items
        : items.filter((o) => o.status !== "closed" && o.status !== "cancelled")
      )
        .slice(0, 20)
        .map((o) => ({
          id: o.id,
          status: o.status,
          type: o.type,
          tableName: o.table?.name ?? null,
          total: o.total,
          itemCount: Array.isArray(o.items) ? o.items.length : undefined,
          createdAt: o.createdAt,
        }));

      if (ctx.follow.enabled) {
        await ctx.follow.step(`✓ ${active.length} orden(es)`, {
          durationMs: 500,
          spinner: false,
        });
        ctx.follow.clear();
      }

      return {
        ok: true,
        result: { count: active.length, orders: active },
        humanSummary: `${active.length} orden(es) activas.`,
      };
    } catch (err) {
      return {
        ok: false,
        error: (err as Error)?.message ?? "Error obteniendo órdenes.",
      };
    }
  },
};
